import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { messageAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { MessageCircle, ArrowLeft } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';

export default function MessagesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversations();
    
    // Listen for new messages
    const handleWsMessage = (event) => {
      const data = event.detail;
      if (data.type === 'new_message') {
        loadConversations();
      }
    };
    window.addEventListener('ws-message', handleWsMessage);
    return () => window.removeEventListener('ws-message', handleWsMessage);
  }, []);

  const loadConversations = async () => {
    try {
      const response = await messageAPI.getConversations();
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="messages-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="px-6 py-4">
          <h1 className="font-heading text-2xl text-white">Messages</h1>
          <p className="text-sm text-gray-400 font-body">
            {conversations.length} {conversations.length === 1 ? 'conversation' : 'conversations'}
          </p>
        </div>
      </header>

      {conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-dark-elevated flex items-center justify-center mb-4">
            <MessageCircle className="w-10 h-10 text-gray-500" />
          </div>
          <h2 className="font-heading text-xl text-white mb-2">No Messages Yet</h2>
          <p className="text-gray-400 font-body mb-6">
            Match with someone and start a conversation!
          </p>
          <button
            onClick={() => navigate('/discover')}
            className="btn-gold px-6 py-3 rounded-full font-body font-semibold"
            data-testid="start-discovering-btn"
          >
            Start Discovering
          </button>
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {conversations.map((conversation, index) => (
            <motion.div
              key={conversation.match_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/chat/${conversation.match_id}`)}
              className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors"
              data-testid={`conversation-${conversation.match_id}`}
            >
              {/* Profile Photo */}
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/10">
                  <img
                    src={conversation.other_user.photo || 'https://via.placeholder.com/56'}
                    alt={conversation.other_user.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {conversation.unread_count > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-black">
                      {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`font-body font-semibold truncate ${
                    conversation.unread_count > 0 ? 'text-white' : 'text-gray-300'
                  }`}>
                    {conversation.other_user.name}
                  </h3>
                  {conversation.last_message && (
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                      {formatDistanceToNow(new Date(conversation.last_message.created_at), { addSuffix: false })}
                    </span>
                  )}
                </div>
                
                {conversation.last_message ? (
                  <p className={`text-sm truncate ${
                    conversation.unread_count > 0 ? 'text-white font-medium' : 'text-gray-400'
                  }`}>
                    {conversation.last_message.sender_id === user?.user_id && (
                      <span className="text-gray-500">You: </span>
                    )}
                    {conversation.last_message.content}
                  </p>
                ) : (
                  <p className="text-sm text-gold">New match - say hello!</p>
                )}
              </div>

              {/* Unread indicator */}
              {conversation.unread_count > 0 && (
                <div className="w-3 h-3 rounded-full bg-gold flex-shrink-0" />
              )}
            </motion.div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
